import useOffers from 'hooks/Offers/useOffers'
import useOrder from 'hooks/Order/useOrder'
import { usePlaceByCoords } from 'hooks/usePlace'
import { getOrderItemsEligibleForCarHireBundledDiscount } from 'lib/carHire/carHireUtils'
import { useMemo } from 'react'
import useCarHireOfferListFilters from './useCarHireOfferListFilters'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { findCarHireListKey, getCarHireListKey } from 'components/CarHire/carHireUtils'
import { useCarHireSearchLink } from 'checkout/Components/Confirmation/BookingDetailsV2/UpsellTiles/useCarHireSearchLink'
import { dateIsAfter } from 'lib/datetime/dateUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'

const emptyCarHireList: App.CarHireList = {
  vehicles: [],
  fetching: true,
  bundledOrder: undefined,
  pickUp: { date: '', dateChanged: false },
  dropOff: { date: '', dateChanged: false },
}

const emptyCheapsetList: App.CarHireCheapestOfferList = {
  offerSummary: undefined,
  fetching: true,
}

const EMPTY_ITEMS_RESULT = {
  earliestItem: undefined,
  latestItem: undefined,
  items: [],
}

function useOrderItemsEligibleForCarHireBundledDiscount(orderId?: string) {
  const [order, isFetchingOrder] = useOrder(orderId)

  const offerIds = useMemo(() => {
    if (!order) return []
    return order.items.map((i) => i.offer.id)
  }, [order])

  const [offers, isFetchingOffers] = useOffers(offerIds)

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)

  const itemsResult = useMemo(() => {
    if (!order) {
      return EMPTY_ITEMS_RESULT
    }

    return getOrderItemsEligibleForCarHireBundledDiscount(order, offers)
  }, [order, offers])

  const [place, fetchingPlace] = usePlaceByCoords(itemsResult.earliestItem?.latitude, itemsResult.earliestItem?.longitude)

  const { pickUpDate, dropOffDate } = useMemo(() => {
    if (!itemsResult.earliestItem || !itemsResult.latestItem) return { pickUpDate: undefined, dropOffDate: undefined }

    let pickUpDate = itemsResult.earliestItem.startDate
    const dropOffDate = itemsResult.latestItem.endDate
    const now = new Date()

    // If hotel check-in has already passed, use today's date as the pick-up date
    if (dateIsAfter(now, new Date(pickUpDate))) {
      pickUpDate = moment(now).format(ISO_DATE_FORMAT)
    }

    return {
      pickUpDate,
      dropOffDate,
    }
  }, [itemsResult.earliestItem, itemsResult.latestItem])

  const filters = useCarHireOfferListFilters({
    pickUpDate,
    dropOffDate,
    pickUpTime: '10:00',
    dropOffTime: '10:00',
    pickUpLocationId: place?.id,
    dropOffLocationId: place?.id,
    driversAgeCategory: defaultAgeCategory,
    isPickUpAtAirport: false,
    pickUpSearchType: 'geo',
    dropOffSearchType: 'geo',
    driversAge: defaultAge,
    bundledOrderId: order?.id,
    pickUpLatitude: itemsResult.earliestItem?.latitude,
    pickUpLongitude: itemsResult.earliestItem?.longitude,
    dropOffLatitude: itemsResult.latestItem?.latitude,
    dropOffLongitude: itemsResult.latestItem?.longitude,
  })

  const [searchLink] = useCarHireSearchLink(
    itemsResult.earliestItem?.startDate,
    itemsResult.latestItem?.endDate,
    itemsResult.earliestItem?.latitude,
    itemsResult.earliestItem?.longitude,
    order?.id,
  )

  const listKey = useMemo(() => getCarHireListKey(filters), [filters])

  const {
    vehicles,
    fetching: fetchingVehiclesList,
    bundledOrder: listBundledOrder,
  } = useAppSelector(state => findCarHireListKey(state.carHire.offerLists, listKey)) ?? emptyCarHireList

  const {
    offerSummary: cheapestVehicle,
    fetching: fetchingCheapestVehicle,
  } = useAppSelector(state => state.carHire.cheapestOfferLists[listKey]) ?? emptyCheapsetList

  const hasVehicles = vehicles.length > 0 || !!cheapestVehicle
  const fetchingVehicles = fetchingVehiclesList && fetchingCheapestVehicle
  const bundledOrderDetails = listBundledOrder ?? cheapestVehicle?.bundledOrder

  return {
    ...(hasVehicles || fetchingVehicles ? itemsResult : EMPTY_ITEMS_RESULT),
    place,
    fetching: isFetchingOrder || isFetchingOffers || fetchingPlace || fetchingVehicles,
    filters,
    searchLink,
    extendedRentalStartDate: bundledOrderDetails?.extendedRentalStartDate,
    extendedRentalEndDate: bundledOrderDetails?.extendedRentalEndDate,
  }
}

export default useOrderItemsEligibleForCarHireBundledDiscount
